"use client";
import React, { startTransition, useEffect, useState } from "react";
import { MagicWand } from "iconoir-react";
import { FormCard } from "@/forms/FormCard";
import classNames from "classnames";
import { FormGroup } from "@/forms/FormGroup";
import { EmailField } from "@/forms/customer/EmailField";
import { PhoneField } from "@/forms/customer/PhoneField";
import { fillDataFactoryFields } from "@/checkout/utils";
import { useFormContext, useWatch } from "react-hook-form";
import { CheckoutForm } from "@/checkout/types";
import { dataFactorySearch } from "@/checkout/subscription/_actions";
import { PHONE_NUMBER_REGEX_ALLOW_SPACE } from "@cappelendamm/e-commerce-services";
import { Typography } from "@cappelendamm/design-system/react";
import { getIsProduction } from "@/checkout/Sections/CustomerSection/actions";
import { useAuth } from "@clerk/nextjs";
import { getIsTeacherFromSessionClaims } from "@/utils/getIsTeacherFromSessionClaims";

type ContactInformationGroupProps = {
  isDisabled: boolean;
};

export const ContactInformationGroup = ({
  isDisabled,
}: ContactInformationGroupProps) => {
  const { setValue, control } = useFormContext<CheckoutForm>();
  const [dataFactoryResultFound, setDataFactoryResultFound] =
    useState<boolean>(false);
  const { sessionClaims } = useAuth();
  const isTeacher = getIsTeacherFromSessionClaims(sessionClaims);

  const phone = useWatch({ control, name: "phone" });

  useEffect(() => {
    startTransition(async () => {
      setDataFactoryResultFound(false);
      const fillFields = async () => {
        const retrievedCustomer = await dataFactorySearch(phone);
        if (retrievedCustomer) {
          fillDataFactoryFields(setValue, retrievedCustomer);
        }
        setDataFactoryResultFound(!!retrievedCustomer);
      };

      const isProduction = await getIsProduction();
      if (
        phone &&
        PHONE_NUMBER_REGEX_ALLOW_SPACE.test(phone) &&
        isProduction &&
        !isTeacher
      ) {
        fillFields();
      }
    });
  }, [setValue, phone]);

  return (
    <div>
      <FormCard
        className={classNames({
          "rounded-b-none border-b-0": dataFactoryResultFound,
        })}
      >
        <FormGroup isDisabled={isDisabled}>
          <h3>
            <Typography as="legend" variant="title-md">
              Kontaktinformasjon
            </Typography>
          </h3>
          <EmailField />
          <PhoneField
            tooltip={{
              expandUpwards: true,
              expandLeftwards: true,
              children: (
                <p>
                  <b>Telefonnummer:</b> Fyll inn ditt telefonnummer for å gjøre
                  utfyllingen av skjemaet enklere.
                </p>
              ),
            }}
          />
        </FormGroup>
      </FormCard>
      {dataFactoryResultFound && (
        <FormCard className="rounded-t-none border-[#ACAEFF] bg-linear-to-r from-[#F1DBFF] to-[#D1E1FA]">
          <div className="flex gap-2">
            <MagicWand width={24} height={24} />
            <div>Vi fylte inn informasjon fra offentlige registre</div>
          </div>
        </FormCard>
      )}
    </div>
  );
};
